/**
 * Lumina session token resolution.
 *
 * AIRI normally stores provider API keys in localStorage
 * (`settings/credentials/providers`). For the Lumina deployment that is not
 * acceptable: our OpenRouter and ElevenLabs keys would be readable from
 * devtools by anyone who loads the page. Instead, all provider calls go to the
 * Lumina credential proxy, which injects the real keys server-side, and the
 * only thing the browser ever holds is the signed-in user's short-lived JWT.
 *
 * That JWT is therefore what we pass as the "API key". It is deliberately NOT
 * persisted into the provider credential store — the stored value is the
 * sentinel below, and the live token is resolved at call time so that an
 * expired session fails cleanly with a 401 from the proxy rather than silently
 * replaying a stale token forever.
 */

/**
 * Placeholder written into provider config so AIRI's "is this configured?"
 * validation passes. It is not a credential and grants nothing.
 */
export const LUMINA_SESSION_SENTINEL = 'lumina-session'

/**
 * The proxy is exposed by Caddy at /api/airi/*, so this stays same-origin: it
 * works across luminacorp.io, a preview host, or a tailnet address with no
 * rebuild.
 */
const LUMINA_PROXY_BASE_PATH = '/api/airi/v1/'

/**
 * Makes a same-origin base URL absolute.
 *
 * NOTICE:
 * A bare path cannot be the base here. The model-listing path builds request
 * URLs with `new URL(baseUrl)`, which throws `Invalid base URL` on a relative
 * value — so listing models against the proxy failed before any request was
 * sent, and the failure surfaced only as a console error. Resolving against the
 * current origin keeps the deploy-anywhere property while satisfying `URL`.
 * Accepts a stored value too, so configs persisted before this fix are healed
 * on read instead of needing a migration.
 */
export function resolveLuminaBaseUrl(configured?: string): string {
  const candidate = configured?.trim() || LUMINA_PROXY_BASE_PATH
  if (/^https?:\/\//i.test(candidate))
    return candidate
  if (typeof window === 'undefined')
    return candidate
  return new URL(candidate, window.location.origin).toString()
}

export const LUMINA_PROXY_BASE_URL = resolveLuminaBaseUrl()

function readCookie(name: string): string | null {
  if (typeof document === 'undefined')
    return null
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${escaped}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

function readStorage(key: string): string | null {
  try {
    const v = localStorage.getItem(key)
    return v && v.trim() !== '' ? v.trim() : null
  }
  catch {
    return null // private mode / blocked storage
  }
}

/**
 * The live JWT, tried in order of how authoritative the source is:
 *
 *  1. `window.__LUMINA_JWT__` — injected by the host page. Most explicit, and
 *     the right answer when AIRI is embedded in the platform shell.
 *  2. `lumina_token` cookie — the existing cross-service SSO cookie already
 *     used by AionUI's AuthContext, so a user signed into the platform is
 *     signed into AIRI with no extra step.
 *  3. localStorage `lumina_jwt` / `lumina_token` — same-origin fallback for
 *     when the platform stores its session there.
 *
 * Returns null when there is no session; callers should let the proxy answer
 * 401 rather than inventing a token.
 */
export function getLuminaSessionToken(): string | null {
  if (typeof window !== 'undefined') {
    const injected = (window as unknown as { __LUMINA_JWT__?: unknown }).__LUMINA_JWT__
    if (typeof injected === 'string' && injected.trim() !== '')
      return injected.trim()
  }
  return readCookie('lumina_token') ?? readStorage('lumina_jwt') ?? readStorage('lumina_token')
}

/**
 * Resolve the value to send as the bearer token.
 *
 * `configured` is whatever the provider store holds. If it is the sentinel (or
 * empty) we substitute the live session token. If an operator has deliberately
 * set a real value we honour it, so self-hosted or offline setups are not
 * broken by this deployment's choices.
 */
export function resolveLuminaApiKey(configured?: string): string {
  const trimmed = typeof configured === 'string' ? configured.trim() : ''
  if (trimmed && trimmed !== LUMINA_SESSION_SENTINEL)
    return trimmed
  return getLuminaSessionToken() ?? ''
}
